export interface State {
  zone: { ts: number; current_temp: number | null; humidity: number | null; target_temp: number | null; heating_power: number | null; mode: string | null; overlay_active: 0 | 1; overlay_until: number | null } | null;
  presence: { ts: number; at_home: 0 | 1; forced: 0 | 1 } | null;
  weather: { ts: number; outside_temp: number | null; solar_intensity: number | null; weather_state: string | null } | null;
  fetched_at: number | null;
  staleness_seconds: number | null;
}

export interface Quota { calls_used: number; remaining: number | null; reset_at: string; reset_date: string }
export interface SetupStatus { install_complete: boolean; tado_connected: boolean }

export interface PhotoMeta {
  id: number;
  thumb_url: string;
  full_url: string;
  width: number | null;
  height: number | null;
  created_at: number;
}
export interface UploadResult {
  uploaded: { id: number }[];
  rejected: { name: string; reason: string }[];
}
export interface HouseConfig { rules_url: string | null; telescope_url: string | null }

export interface Camera {
  id: string;
  name: string;
  type: 'welcome' | 'presence';
  monitoring: 'on' | 'off';
  floodlight: 'auto' | 'on' | 'off' | null;
  reachable: boolean;
  snapshot_url: string;
}
export interface NetatmoStatus { configured: boolean; connected: boolean }

export interface RuntimeMetric { hours: number; kwh: number | null; eur: number | null }
export interface RuntimeSummary {
  week: RuntimeMetric;
  month: RuntimeMetric;
  year: RuntimeMetric;
  power_kw: number | null;
  tariff_eur_per_kwh: number | null;
}
export type RuntimeBucket = 'day' | 'week' | 'month' | 'year';
export interface RuntimeHistoryPoint extends RuntimeMetric { bucket: string }
export interface RuntimeHistory {
  bucket: RuntimeBucket;
  points: RuntimeHistoryPoint[];
  power_kw: number | null;
  tariff_eur_per_kwh: number | null;
}
export interface EnergyConfig { power_kw: number | null; tariff_eur_per_kwh: number | null }

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const isForm = init.body instanceof FormData;
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: { ...(isForm ? {} : { 'content-type': 'application/json' }), ...(init.headers ?? {}) },
    ...init,
  });
  if (res.status === 401) throw new ApiError('unauthenticated', 401);
  if (!res.ok) throw new ApiError(await res.text().catch(() => res.statusText), res.status);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export class ApiError extends Error { constructor(message: string, public status: number) { super(message); } }

export const api = {
  getStatus: () => req<SetupStatus>('/setup/status'),
  setupPassword: (password: string) => req('/setup/password', { method: 'POST', body: JSON.stringify({ password }) }),
  startTado: () => req<{ verification_uri_complete: string; user_code: string; interval: number }>('/setup/tado/start', { method: 'POST' }),
  pollTado: () => req<{ status: 'pending' | 'connected' }>('/setup/tado/poll', { method: 'POST' }),
  finalizeTado: () => req<{ home_id: number; zone_id: number }>('/setup/tado/finalize', { method: 'POST' }),
  login: (password: string) => req('/auth/login', { method: 'POST', body: JSON.stringify({ password }) }),
  logout: () => req('/auth/logout', { method: 'POST' }),
  me: () => req<{ authenticated: true }>('/auth/me'),
  getState: () => req<State>('/api/state'),
  getQuota: () => req<Quota>('/api/quota'),
  getWrites: (limit = 20) => req<{ entries: { ts: number; action: string; payload: unknown; result: string }[] }>(`/api/writes?limit=${limit}`),
  getHistory: (from: number, to: number, resolution: 'minute' | 'hour' | 'day') =>
    req<{ points: { ts: number; current_temp: number | null; humidity: number | null; target_temp: number | null; heating_power: number | null; outside_temp: number | null; at_home: number | null }[] }>(`/api/history?from=${from}&to=${to}&resolution=${resolution}`),
  setOverride: (temperature: number, termination: { type: 'MANUAL' } | { type: 'TADO_MODE' } | { type: 'TIMER'; seconds: number }) =>
    req('/api/override', { method: 'POST', body: JSON.stringify({ temperature, termination }) }),
  endOverride: () => req('/api/override', { method: 'DELETE' }),
  setPresence: (at_home: boolean) => req('/api/presence', { method: 'POST', body: JSON.stringify({ at_home }) }),
  clearPresence: () => req('/api/presence', { method: 'DELETE' }),
  refreshNow: () => req<State>('/api/refresh-now', { method: 'POST' }),
  listPhotos: () => req<{ photos: PhotoMeta[] }>('/api/photos'),
  uploadPhotos: (files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append('file', f);
    return req<UploadResult>('/api/photos', { method: 'POST', body: fd });
  },
  deletePhoto: (id: number) => req(`/api/photos/${id}`, { method: 'DELETE' }),
  getHouseConfig: () => req<HouseConfig>('/api/house'),
  // Omitted keys are left untouched server-side; an empty string clears one.
  setHouseConfig: (patch: { rules_url?: string; telescope_url?: string }) =>
    req('/api/house', { method: 'PUT', body: JSON.stringify(patch) }),
  netatmoStatus: () => req<NetatmoStatus>('/api/netatmo/status'),
  netatmoAuthorizeUrl: () => req<{ url: string }>('/api/netatmo/authorize-url'),
  netatmoDisconnect: () => req('/api/netatmo/disconnect', { method: 'POST' }),
  listCameras: () => req<{ cameras: Camera[] }>('/api/cameras'),
  setMonitoring: (id: string, state: 'on' | 'off') =>
    req(`/api/cameras/${encodeURIComponent(id)}/monitoring`, { method: 'PUT', body: JSON.stringify({ state }) }),
  setFloodlight: (id: string, mode: 'auto' | 'on' | 'off') =>
    req(`/api/cameras/${encodeURIComponent(id)}/floodlight`, { method: 'PUT', body: JSON.stringify({ mode }) }),
  getRuntimeSummary: () => req<RuntimeSummary>('/api/heating-runtimes/summary'),
  getRuntimeHistory: (bucket: RuntimeBucket, from: string, to: string) =>
    req<RuntimeHistory>(`/api/heating-runtimes/history?bucket=${bucket}&from=${from}&to=${to}`),
  refreshRuntimes: () => req<{ daysUpserted: number; totalSeconds: number }>('/api/heating-runtimes/refresh', { method: 'POST' }),
  getEnergyConfig: () => req<EnergyConfig>('/api/config/energy'),
  setEnergyConfig: (cfg: EnergyConfig) =>
    req('/api/config/energy', { method: 'PUT', body: JSON.stringify(cfg) }),
};
